import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactSelect from "react-select";
import {
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/20/solid";
import { getEvaluatorQuestionsByUUID, getStatuses } from "../../services/questionService";
import { filterSelectStyles, selectTheme } from "../../components/forms/Select/styles";
import FilterChips, { CHIP_TONES } from "../../components/QuestionList/FilterChips";
import FilterPanel from "../../components/QuestionList/FilterPanel";
import ResultsBar, { Pager } from "../../components/QuestionList/ResultsBar";
import { QuestionStatusBadge, STATUS_ORDER, statusText } from "../../components/QuestionList/questionStatus";
import { codeUnderName } from "../../components/QuestionList/cells";
import { focusRing, useMediaQuery } from "../Exams/TestBuilder/ui";
import { UserContext } from "../../utils/contexts/UserContext";
import { isOpenClick } from "../../utils/rowLink";
import { isCanceledError } from "../../utils/examError";
import { formatDateTime } from "../../utils/formatDate";
import { canOpenPage } from "../../utils/permissions";
import { HelpTitle } from "../../components/Help/HelpButton";
import MyEvaluationsGuide from "../../components/Help/guides/MyEvaluationsGuide";

const PAGE_SIZE = 20;
// The search applies this long after the typing stops.
const SEARCH_DELAY = 350;

// My vote as the API gives it (evaluation_value) and its value for the vote filter.
const VOTES = {
  Upvoted: { filter: "1", label: "evaluators.upvoted", Icon: HandThumbUpIcon, tone: CHIP_TONES.green, quote: "border-emerald-300" },
  Downvoted: { filter: "-1", label: "evaluators.downvoted", Icon: HandThumbDownIcon, tone: CHIP_TONES.red, quote: "border-red-300" },
};

// evaluation_date is Python's text of a datetime ("2025-12-02 09:42:39.151196+00:00"). With a "T" and
// milliseconds it is an ISO date every browser reads (Safari has no date for the form with a space).
const isoDate = (value) =>
  typeof value === "string" ? value.trim().replace(" ", "T").replace(/(\.\d{3})\d+/, "$1") : value;

const statusRank = (code) => {
  const index = STATUS_ORDER.indexOf(code);
  return index === -1 ? STATUS_ORDER.length : index;
};

const questionHref = (question) => `/questions/${question.uuid}`;

const secondaryButtonClass = `inline-flex h-9 items-center rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 ${focusRing}`;
// 44px on phones: it is the empty page's one control.
const primaryButtonClass = `inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 sm:h-10 ${focusRing}`;

/**
 * One evaluation: the question (its name is the row's one link, with a new-tab icon; it opens in a new tab, as a
 * click on the row does, so the list keeps its filters and page), its status, my vote with its date, and my
 * comment. Wide screens lay it out as a row of two columns; narrower ones put the vote and the comment under the
 * question.
 */
function EvaluationItem({ evaluation, wide, language }) {
  const [t] = useTranslation();
  const href = questionHref(evaluation);
  const vote = Object.prototype.hasOwnProperty.call(VOTES, evaluation.evaluation_value) ? VOTES[evaluation.evaluation_value] : null;
  const VoteIcon = vote?.Icon;
  const when = isoDate(evaluation.evaluation_date);
  const date = formatDateTime(when, language);
  const dateLabel = t("evaluators.evaluated_on", { date });
  const subtopic = typeof evaluation.subtopic === "string" ? evaluation.subtopic : evaluation.subtopic?.name;
  const code = codeUnderName(evaluation);
  const meta = [code, subtopic].filter(Boolean);
  // Older votes saved the text "no comment" for an empty comment (the server no longer does): shown as none.
  const rawComment = typeof evaluation.evaluation_comment === "string" ? evaluation.evaluation_comment.trim() : "";
  const comment = rawComment.toLowerCase() === "no comment" ? "" : rawComment;
  const newTab = t("evaluators.my_evaluations.new_tab");

  const open = (event) => {
    if (isOpenClick(event)) window.open(href, "_blank", "noopener");
  };

  return (
    <li
      onClick={open}
      onAuxClick={(event) => event.button === 1 && open(event)}
      className={
        wide
          ? "cursor-pointer px-5 py-4 transition-colors duration-150 hover:bg-slate-50/80"
          : "cursor-pointer rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm transition duration-200 hover:border-slate-300"
      }
    >
      <div className="grid grid-cols-1 gap-y-2.5 xl:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] xl:gap-x-8 2xl:grid-cols-[minmax(0,24rem)_minmax(0,1fr)]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={t("evaluators.view_question")}
              className={`group relative min-w-0 break-words rounded text-[15px] font-semibold leading-snug text-slate-900 transition-colors duration-200 hover:text-blue-700 ${focusRing}`}
            >
              {evaluation.name || evaluation.code || "—"}
              <ArrowTopRightOnSquareIcon
                className="ml-1 inline-block h-3.5 w-3.5 align-[-0.125em] text-slate-400 transition-colors duration-200 group-hover:text-blue-600"
                aria-hidden="true"
              />
              <span className="sr-only"> ({newTab})</span>
            </a>
            <QuestionStatusBadge status={evaluation.status} />
          </div>
          {meta.length > 0 && (
            <p className="mt-0.5 break-words text-sm text-slate-500">
              {meta.map((part, index) => (
                <React.Fragment key={index}>
                  {index > 0 && " · "}
                  <span className={index === 0 && code ? "font-mono text-xs" : undefined}>{part}</span>
                </React.Fragment>
              ))}
            </p>
          )}
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {vote && (
              <span
                className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${vote.tone.badge}`}
              >
                <VoteIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {t(vote.label)}
              </span>
            )}
            {date && (
              <span className="relative inline-flex items-center gap-1 text-sm text-slate-500" title={dateLabel}>
                <ClockIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                <span className="sr-only">{dateLabel}</span>
                <time dateTime={when} aria-hidden="true" className="tabular-nums">
                  {date}
                </time>
              </span>
            )}
          </div>
          {comment ? (
            <blockquote className={`relative mt-2 max-w-3xl border-l-2 pl-3 ${vote ? vote.quote : "border-slate-200"}`}>
              <span className="sr-only">{t("evaluators.my_evaluations.my_comment")} </span>
              <p className="whitespace-pre-line break-words text-sm leading-relaxed text-slate-700">{comment}</p>
            </blockquote>
          ) : (
            <p className="mt-2 text-sm italic text-slate-500">{t("evaluators.no_comment")}</p>
          )}
        </div>
      </div>
    </li>
  );
}

export default function MyEvaluations() {
  const [t, i18n] = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const { user } = useContext(UserContext);
  const userUuid = user?.uuid;
  // Wide screens: the evaluations as rows of one card, as in a table; phones: one card each.
  const wide = useMediaQuery("(min-width: 768px)");
  // Phones' search box is narrow (the filters toggle is beside it): a shorter placeholder.
  const roomySearch = useMediaQuery("(min-width: 640px)");
  // The results line: a page change brings the focus back to it (goToPage).
  const resultsRef = useRef(null);

  // The search box's text; `search` is the one applied, SEARCH_DELAY later.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // "all", or the vote filter's value ("1" up, "-1" down); the status: "all" or a status uuid.
  const [vote, setVote] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [statusOptions, setStatusOptions] = useState([]);

  const [evaluations, setEvaluations] = useState([]);
  // null until the first answer; a failed request keeps the last count known.
  const [count, setCount] = useState(null);
  // Whether an answer ever had an evaluation: from then on the filters stay, whatever a filter leaves, so they
  // never vanish under a click or the typing.
  const [hasAny, setHasAny] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // "Try again" bumps it to send the same request again.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    getStatuses({ params: { page: 1, page_size: 100 }, signal: controller.signal })
      .then((data) => setStatusOptions(Array.isArray(data) ? data : data?.results || []))
      .catch((err) => {
        if (!isCanceledError(err)) console.error("Failed to fetch statuses:", err);
      });
    return () => controller.abort();
  }, []);

  // A new search starts from page 1.
  useEffect(() => {
    const value = searchInput.trim();
    if (value === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  // One page of my evaluations; a newer request aborts the older one.
  useEffect(() => {
    if (!userUuid) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    getEvaluatorQuestionsByUUID({
      uuid: userUuid,
      params: {
        page,
        page_size: PAGE_SIZE,
        search: search || undefined,
        evaluation_value: vote !== "all" ? vote : undefined,
        status: status !== "all" ? status : undefined,
      },
      signal: controller.signal,
    })
      .then((data) => {
        const results = Array.isArray(data) ? data : data?.results || [];
        // An empty page past the first one (as a 404 below): back to the first one.
        if (!results.length && page > 1) {
          setPage(1);
          return;
        }
        const total = typeof data?.count === "number" ? data.count : results.length;
        setEvaluations(results);
        setCount(total);
        if (total > 0) setHasAny(true);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || isCanceledError(err)) return;
        // A page that no longer exists (fewer evaluations than before): back to the first one.
        if (err?.response?.status === 404 && page > 1) {
          setPage(1);
          return;
        }
        console.error("Failed to fetch my evaluations:", err);
        setEvaluations([]);
        setFailed(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [userUuid, page, search, vote, status, attempt]);

  // Every filter change starts from page 1.
  const changeVote = (value) => {
    setVote(value);
    setPage(1);
  };
  const changeStatus = (value) => {
    setStatus(value);
    setPage(1);
  };
  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setVote("all");
    setStatus("all");
    setPage(1);
  };

  // While the page loads the pagers are disabled or hidden, so they lose the focus: it goes to the results
  // line. The bottom pager also brings that line (the top of the list) into view.
  const goToPage = (next, { scroll = false } = {}) => {
    setPage(next);
    resultsRef.current?.focus({ preventScroll: true });
    if (scroll) resultsRef.current?.scrollIntoView({ block: "start" });
  };

  const pageCount = Math.ceil((count || 0) / PAGE_SIZE);
  const filtersActive = searchInput.trim() !== "" || vote !== "all" || status !== "all";
  // The list shown answers the filters applied (the search applies after its pause): its empty state follows them,
  // so an erased search never flashes "not evaluated yet" before the list comes back.
  const listFiltered = search !== "" || vote !== "all" || status !== "all";
  // Those who vote (the question page's rule: vote_question without approve_question) gather evaluations: their
  // filters are drawn while the first page loads, so the list does not push them in.
  const perms = user?.perms || [];
  const votes = perms.includes("vote_question") && !perms.includes("approve_question");
  // The vote chips, the filters and the count once there is something to filter: someone with no evaluations yet
  // sees the empty state and its one button alone.
  const showFilters = hasAny || filtersActive || (votes && loading && count === null);
  const canOpenBank = canOpenPage(user, "/questions");

  const voteItems = [VOTES.Upvoted, VOTES.Downvoted].map((v) => ({ value: v.filter, label: t(v.label), tone: v.tone }));

  // Statuses in the order a question goes through them, named as on the badges.
  const statusSelectOptions = [
    { value: "all", label: t("exam_taking.all_statuses") },
    ...[...statusOptions]
      .sort((a, b) => statusRank(a.code) - statusRank(b.code))
      .map((s) => ({ value: s.uuid, label: statusText(t, s.code, s.name) })),
  ];

  const filters = [
    {
      key: "status",
      active: status !== "all",
      node: (
        <ReactSelect
          className="relative"
          aria-label={t("question_list.status_filter")}
          styles={filterSelectStyles}
          theme={selectTheme}
          options={statusSelectOptions}
          value={statusSelectOptions.find((o) => o.value === status) || statusSelectOptions[0]}
          onChange={(option) => changeStatus(option?.value || "all")}
          placeholder={t("exam_taking.all_statuses")}
          noOptionsMessage={() => t("table.no_options")}
          isSearchable={false}
        />
      ),
    },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-5 motion-safe:animate-fade-in">
      <div>
        {/* The guide's "?" right after the title. */}
        <HelpTitle
          helpId="my_evaluations"
          helpTitle={t("help.my_evaluations.title")}
          guide={<MyEvaluationsGuide seesExperts={canOpenPage(user, "/evaluators")} hasEvaluations={hasAny} />}
        >
          {t("evaluators.my_evaluations.title")}
        </HelpTitle>
        <p className="mt-1 text-sm text-slate-500">{t("evaluators.my_evaluations.subtitle")}</p>
      </div>

      {showFilters && (
        <>
          <FilterChips
            label={t("evaluators.my_evaluations.vote_filter")}
            allLabel={t("question_list.status_all")}
            items={voteItems}
            selected={vote}
            onSelect={changeVote}
          />

          <FilterPanel
            search={searchInput}
            onSearchChange={setSearchInput}
            searchLabel={t("evaluators.my_evaluations.search_label")}
            searchPlaceholder={t(
              roomySearch ? "evaluators.my_evaluations.search_placeholder" : "evaluators.my_evaluations.search_placeholder_short"
            )}
            filters={filters}
            canClear={filtersActive}
            onClearAll={clearFilters}
          />
        </>
      )}

      {!failed && showFilters && (
        <div ref={resultsRef} tabIndex={-1} className="scroll-mt-4 focus:outline-none">
          <ResultsBar
            countText={count === null ? " " : t("evaluators.my_evaluations.count", { count })}
            page={page}
            pageCount={pageCount}
            onPage={(next) => goToPage(next)}
            loading={loading}
          />
        </div>
      )}

      {failed ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center shadow-sm">
          <div className="mb-3 rounded-full bg-red-50 p-3 text-red-500">
            <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />
          </div>
          <p role="alert" className="text-sm font-medium text-slate-900">
            {t("evaluators.my_evaluations.load_failed")}
          </p>
          <button type="button" onClick={() => setAttempt((n) => n + 1)} className={`mt-4 ${secondaryButtonClass}`}>
            {t("evaluators.my_evaluations.retry")}
          </button>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 shadow-sm">
          <div className="text-center" role="status">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" aria-hidden="true"></div>
            <p className="text-sm text-slate-500">{t("evaluators.loading_questions")}</p>
          </div>
        </div>
      ) : evaluations.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center shadow-sm">
          <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400">
            {listFiltered ? (
              <MagnifyingGlassIcon className="h-6 w-6" aria-hidden="true" />
            ) : (
              <ClipboardDocumentCheckIcon className="h-6 w-6" aria-hidden="true" />
            )}
          </div>
          <h3 className="text-sm font-medium text-slate-900">
            {listFiltered ? t("evaluators.no_evaluations") : t("evaluators.my_evaluations.empty_title")}
          </h3>
          <p className="mt-1 max-w-md text-sm text-slate-500">
            {listFiltered ? t("evaluators.no_evaluations_hint") : t("evaluators.my_evaluations.empty_hint")}
          </p>
          {/* "Clear all" is the filters card's, right above: not repeated here. */}
          {!listFiltered && canOpenBank && (
            <Link to="/questions" className={`mt-4 ${primaryButtonClass}`}>
              {t("evaluators.my_evaluations.go_to_bank")}
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          )}
        </div>
      ) : (
        <ul
          className={
            wide
              ? "divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              : "flex flex-col gap-2.5"
          }
        >
          {evaluations.map((evaluation) => (
            <EvaluationItem key={evaluation.uuid} evaluation={evaluation} wide={wide} language={language} />
          ))}
        </ul>
      )}

      {!failed && evaluations.length > 0 && pageCount > 1 && (
        <div className="flex justify-center">
          <Pager
            page={page}
            pageCount={pageCount}
            onPage={(next) => goToPage(next, { scroll: true })}
            disabled={loading}
            label={t("question_list.pagination_bottom")}
          />
        </div>
      )}
    </div>
  );
}
